import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Document, Page, pdfjs } from 'react-pdf'

import { FormLayout, Translate, getSignerName, SubmitButton } from '@ana/shared'
import { Loader } from '@ana/shared'
import { LayoutItem } from '@ana/shared'
import { CircularProgress, Grid } from '@mui/material'
import PageLayout from 'components/PageLayout'
import { Field, FieldProps, Formik } from 'formik'
import range from 'lodash/range'

import SignatureModal from '../../components/SignatureModal'
import { PageProps } from '../../hooks/usePage'

import * as S from './SignDocument.styles'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

type DocumentLoadPros = {
  numPages: number
}

export type Props = PageProps & {
  pdfLink?: string
  onSignatureSubmit: (signature: string) => void
  signatureLoading?: boolean
  titleKey: string
  subtitleKey: string
  descriptionKey: string
}

const SignDocument: React.FC<Props> = ({
  initialValues = {},
  pdfLink,
  signatureLoading,
  onSignatureSubmit,
  titleKey,
  subtitleKey,
  descriptionKey,
}) => {
  const { t, i18n } = useTranslation()
  const [signature, setSignature] = useState<string | undefined>()
  const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false)
  const [isSignatureSubmitted, setIsSignatureSubmitted] = useState<boolean>(false)
  const [pdfStatus, setPdfStatus] = useState<'loading' | 'error' | 'success'>('success')

  const [numPages, setNumPages] = useState<number>(0)

  const pdfContainerRef = useRef<HTMLDivElement | null>(null)
  const [pdfPageWidth, setPdfPageWidth] = useState<number | undefined>()

  useEffect(() => {
    const node = pdfContainerRef.current

    if (!node) return

    const updateWidth = () => setPdfPageWidth(node.clientWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const pdfLoading = pdfStatus === 'loading'

  const handleSubmit = useCallback(() => {
    if (!isSignatureSubmitted) {
      setShowSignatureModal(true)
    }
  }, [isSignatureSubmitted, setShowSignatureModal])

  const handleSignatureSubmit = useCallback(
    (signature: string) => {
      setSignature(signature)
      setIsSignatureSubmitted(true)

      onSignatureSubmit(signature)
    },
    [setSignature, onSignatureSubmit, setIsSignatureSubmitted]
  )

  const handleSignatureChange = useCallback(
    (signature: string) => {
      setSignature(signature)

      if (!signature) {
        // Reset it when the user clicks to "Clear" the signature pad.
        setIsSignatureSubmitted(false)
      }
    },
    [setSignature, setIsSignatureSubmitted]
  )

  const handleCloseSignatureModal = useCallback(() => {
    setSignature('')
    setShowSignatureModal(false)
  }, [setShowSignatureModal])

  const button = useMemo(
    () => (
      <Field>
        {(props: FieldProps) => {
          return (
            <SubmitButton loading={false} type="submit" onClick={handleSubmit} disabled={!props.form.isValid}>
              <Translate children="sign" />
            </SubmitButton>
          )
        }}
      </Field>
    ),
    [handleSubmit]
  )

  const handlePdfLoadSuccess = ({ numPages }: DocumentLoadPros) => {
    setShowSignatureModal(false)
    setPdfStatus('success')
    setNumPages(numPages)
  }

  const handlePdfLoadError = (_error: Error) => {
    setPdfStatus('error')
  }

  const layout: LayoutItem[] = [
    {
      type: 'custom',
      key: 'signDocumentDescription',
      xs: 12,
      render: () => <S.TextContainer>{t(descriptionKey)}</S.TextContainer>,
    },
    {
      type: 'custom',
      key: 'signDocumentPDF',
      xs: 12,
      render: () => (
        <Grid size={{ xs: 12 }}>
          <S.DocumentPaper elevation={0} ref={pdfContainerRef}>
            {(signatureLoading || pdfLoading) && <CircularProgress size={20} />}

            {!signatureLoading && pdfLink && pdfStatus !== 'error' && (
              <Document
                file={pdfLink}
                loading={<Loader />}
                onLoadSuccess={handlePdfLoadSuccess}
                onLoadError={handlePdfLoadError}
                onSourceError={handlePdfLoadError}
              >
                {range(1, numPages + 1).map((page) => (
                  <Page key={page} pageNumber={page} width={pdfPageWidth} />
                ))}
              </Document>
            )}

            {!signatureLoading && pdfLink && pdfStatus === 'error' && (
              <S.PdfIframe src={pdfLink} frameBorder="0" width="100%" height="auto" language={i18n.language} />
            )}
          </S.DocumentPaper>
        </Grid>
      ),
    },
  ]

  return (
    <PageLayout title={t(titleKey)} subtitle={t(subtitleKey)}>
      <SignatureModal
        isOpen={showSignatureModal}
        name={getSignerName(initialValues)}
        signature={signature}
        onSignatureChange={handleSignatureChange}
        onClose={handleCloseSignatureModal}
        onSubmit={handleSignatureSubmit}
        isSubmitting={signatureLoading || pdfLoading}
      />
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        {(formikProps) => (
          <form onSubmit={formikProps.handleSubmit}>
            <FormLayout items={layout} submitButton={button} />
          </form>
        )}
      </Formik>
    </PageLayout>
  )
}

export default SignDocument
